import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import LeftSideMenu from '../LeftSideMenu';
import { Formik, ErrorMessage } from 'formik';
import { Input, Form as AntdForm, AutoComplete, notification } from 'antd';
import * as Yup from 'yup';
import { Button } from '@ui/components';
import { connect } from 'react-redux';
import { metaSelectors } from '@ui/store/meta';
import { transformFormatSource } from '@ui/tools/transformSource';
import { IRegion } from '@ui/api/v2/regions.api';
import { createCity, ICity, updateCity } from '@ui/api/v2/cities.api';

const { Item } = AntdForm;

const initialValues = {
  name: '',
  region: '',
};

interface IProps {
  city?: ICity;
  regions: IRegion[];
  onCityUpdate: (factory: ICity) => void;
  onCityCreate: (factory: ICity) => void;
  resetEditCity: () => void;
}

const factorySchema = Yup.object().shape({
  name: Yup.string().required('Поле обязательно к заполнению'),
  region: Yup.string().required('Поле обязательно к заполнению'),
});

function CityForm({
  regions,
  city,
  onCityCreate,
  onCityUpdate,
  resetEditCity,
}: IProps) {
  const formikRef = useRef<any>(null);

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      if (city && city.id) {
        resetEditCity();
        const response = await updateCity(city.id, values);
        if (response.data) {
          onCityUpdate(response.data);
        }
      } else {
        const { data, error } = await createCity(values);
        if (data) {
          onCityCreate(data);
        } else {
          notification.error({
            message: error.response.data.message,
          });
        }
      }
      resetForm();
    },
    [city, onCityCreate, onCityUpdate, resetEditCity],
  );

  const cancelEditForm = useCallback(
    (_, { resetForm }) => {
      resetForm();
      resetEditCity();
    },
    [resetEditCity],
  );

  const dataSource = useMemo(() => transformFormatSource(regions), [regions]);

  useEffect(() => {
    if (formikRef.current && city) {
      formikRef.current.setValues({
        name: city.name,
        region: city.region.id.toString(),
      });
    }
  }, [city]);

  const title = useMemo(() => {
    if (city && city.id) {
      return 'Редактировать город';
    }
    return 'Новый город';
  }, [city]);

  return (
    <LeftSideMenu className="cities__left-side" topPanel={title}>
      <Formik
        ref={formikRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onReset={cancelEditForm}
        validationSchema={factorySchema}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          setFieldValue,
          handleReset,
        }) => (
          <form onSubmit={handleSubmit} className="cities__form">
            <Item label="Название">
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                size="large"
                type="text"
                name="name"
              />
              <ErrorMessage
                className="cities__form__error"
                name="name"
                component="div"
              />
            </Item>
            <Item label="Область">
              <AutoComplete
                dataSource={dataSource}
                value={values.region}
                size="large"
                onSelect={value => {
                  setFieldValue('region', value);
                }}
              />
              <ErrorMessage
                name="region"
                className="cities__form__error"
                component="div"
              />
            </Item>
            <div className="cities__buttons-wrapper">
              <Item>
                <Button type="primary" htmlType="submit">
                  Сохранить
                </Button>
              </Item>
              <Item>
                <Button onClick={handleReset}>Отмена</Button>
              </Item>
            </div>
          </form>
        )}
      </Formik>
    </LeftSideMenu>
  );
}

export default connect(state => ({ regions: metaSelectors.getRegions(state) }))(
  memo(CityForm),
);



// WEBPACK FOOTER //
// ./src/components/Cities/CityForm.tsx